import {
  Alert,
  Box,
  Button,
  DatePicker,
  Divider,
  Flex,
  Heading,
  Notice,
  Progress,
  Select,
  Stack,
  TextField,
  TimeField,
} from '@eleveo/components-react';
import { PlusIcon } from '@eleveo/icons-react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { getLocalTimeZone, parseTime, today } from '@internationalized/date';
import { FormApi } from 'final-form';
import { TFunction } from 'i18next';
import { clone } from 'ramda';
import React, { useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useDateFormatter } from 'react-aria';
import { Field, FormSpy } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { OnChange } from 'react-final-form-listeners';

import { useAuth } from '../../app/AuthorizationProvider';
import { Center } from '../../components/LayoutNext/components/Center';
import { minDate } from '../../components/MySchedule/validations';
import { ShiftTemplate, ShiftTemplateType } from '../../graphql/components';
import { toDateFromTime } from '../../utils/date';
import { toSecondsFromTime, toTimeFromSeconds } from '../../utils/time';
import { max255chars, required } from '../../utils/validations';
import { composeValidators } from '../../utils/validations/composeValidators';
import { BreakTemplate } from './BreakTemplate';
import { EvenOddWeekSwitcher } from './EvenOddWeekSwitcher';
import { scheduleActivitiesConfig } from './config';
import {
  ADD_NEW_BREAK_BUTTON,
  getFieldsInputTag,
  SHIFT_BREAKS_CONTAINER,
  SHIFT_END_TIME_INFO,
  SHIFT_SCHEDULE_ACTIVITIES_CONTAINER,
} from './constants';
import { ShiftTemplatesFormValues } from './types';
import { initializeBreakValue, validateDayTemplates } from './utils';

const createShiftTemplateTypeOptions = (t: TFunction) =>
  Object.values(ShiftTemplateType).map((type) => ({
    id: type,
    name: t(`shiftTemplate.type.${type}`),
  }));

interface ShiftTemplateProps {
  currentDayTemplateIndex: number;
  existingShiftTemplates: ShiftTemplate[];
  form: FormApi<ShiftTemplatesFormValues, Partial<ShiftTemplatesFormValues>>;
  isCloning: boolean;
  isCreate: boolean;
  isLoading: boolean;
  setCurrentDayTemplateIndex: (newIndex: number) => void;
  t: TFunction;
}

export const ShiftTemplateForm = ({
  currentDayTemplateIndex,
  existingShiftTemplates,
  isLoading,
  form,
  isCloning,
  isCreate,
  setCurrentDayTemplateIndex,
  t,
}: ShiftTemplateProps) => {
  const { timezone } = useAuth();

  const timeFormatter = useDateFormatter({
    timeStyle: 'short',
  });

  const [isMaximumActivitiesErrorVisible, setMaximumActivitiesErrorVisible] = useState(false);

  if (isLoading) {
    return (
      <Center>
        <Progress />
      </Center>
    );
  }

  return (
    <Box padding={20} testId="shift-template-form">
      <Stack direction="vertical" className="w-full" spacing={25}>
        <fieldset>
          <Stack direction="vertical" className="w-full" spacing={15}>
            <Field
              name="name"
              validate={composeValidators(required, max255chars)}
              render={({ input, meta }) => (
                <TextField
                  {...input}
                  className="w-full"
                  isRequired
                  label={t('shiftTemplate.name')}
                  testId={getFieldsInputTag('shift-name')}
                  validationState={meta.touched && meta.invalid ? 'invalid' : undefined}
                  errorMessage={meta.error}
                />
              )}
            />
            {isCreate && (
              <Field name="cloneShiftTemplateId">
                {({ input }) => (
                  <Select
                    className="w-full"
                    isClearable
                    isDisabled={existingShiftTemplates.length === 0}
                    label={t('form.createFromExisting')}
                    onBlur={input.onBlur}
                    onFocus={input.onFocus}
                    onSelectionChange={(values) => input.onChange(values.values().next().value)}
                    options={existingShiftTemplates}
                    selectedKeys={input.value ? [input.value] : []}
                    selectionMode="single"
                    testId={getFieldsInputTag('clone-shift-template')}
                  />
                )}
              </Field>
            )}
            <Field
              name="effectiveDate"
              validate={isCreate ? composeValidators(required, minDate(today(timezone))) : undefined}
              render={({ input, meta }) => (
                <DatePicker
                  {...input}
                  className="w-full"
                  isReadOnly={!isCreate}
                  isRequired
                  label={t('shiftTemplate.effectiveDate')}
                  minValue={isCreate ? today(timezone) : undefined}
                  onBlur={input.onBlur}
                  onFocus={input.onFocus}
                  testId={getFieldsInputTag('effective-date')}
                  value={input?.value ? input.value : undefined}
                  errorMessage={t(meta.error, { minValue: today(timezone) })}
                  validationState={meta.touched && meta.invalid ? 'invalid' : undefined}
                />
              )}
            />
            <Field name="type" validate={required}>
              {({ input, meta }) => (
                <Select
                  className="w-full"
                  label={t('layout.type')}
                  onBlur={input.onBlur}
                  onFocus={input.onFocus}
                  onSelectionChange={(values) => input.onChange(values.values().next().value)}
                  options={createShiftTemplateTypeOptions(t)}
                  selectedKeys={input.value ? [input.value] : []}
                  selectionMode="single"
                  testId={getFieldsInputTag('shift-type')}
                  validationState={meta.touched && meta.invalid ? 'invalid' : undefined}
                  errorMessage={meta.error}
                />
              )}
            </Field>
          </Stack>
        </fieldset>

        <Field name="type" subscription={{ value: true }}>
          {({ input: { value } }) => {
            if (value === ShiftTemplateType.EvenOdd) {
              return (
                <EvenOddWeekSwitcher
                  currentDayTemplateIndex={currentDayTemplateIndex}
                  form={form}
                  changeCurrentDayTemplateIndex={setCurrentDayTemplateIndex}
                />
              );
            }
            return null;
          }}
        </Field>

        <FieldArray name="dayTemplates" validate={validateDayTemplates}>
          {({ fields: dayTemplatesFields }) =>
            dayTemplatesFields.map((dayTemplateName, dayTemplateIndex) =>
              dayTemplateIndex !== currentDayTemplateIndex ? null : (
                <Stack key={dayTemplateName} direction="vertical" spacing={25} className="w-full">
                  <fieldset>
                    <Stack direction="vertical" className="w-full" spacing={15}>
                      <Heading as="h3" className="text-blue-700">
                        {t('shiftTemplate.time')}
                      </Heading>
                      <Flex gap={15}>
                        <Field name={`${dayTemplateName}.shiftRange.startTime`}>
                          {({ input, meta }) => (
                            <TimeField
                              testId={getFieldsInputTag('shift-start-time')}
                              label={t('shiftTemplate.startTime')}
                              onBlur={input.onBlur}
                              onFocus={input.onFocus}
                              onChange={(value) =>
                                input.onChange(
                                  value
                                    ? `${String(value.hour).padStart(2, '0')}:${String(value.minute).padStart(2, '0')}`
                                    : null
                                )
                              }
                              value={input.value ? parseTime(`${input.value}:00`) : null}
                              steps={{ minute: 15 }}
                              validationState={meta.touched && meta.invalid ? 'invalid' : undefined}
                              className="w-full"
                            />
                          )}
                        </Field>
                        <Field name={`${dayTemplateName}.shiftDuration`}>
                          {({ input, meta }) => (
                            <TimeField
                              testId={getFieldsInputTag('shift-duration')}
                              label={t('time.duration')}
                              onBlur={input.onBlur}
                              onFocus={input.onFocus}
                              onChange={(value) => {
                                input.onChange(value ? toSecondsFromTime(value) : null);
                              }}
                              value={Number.isInteger(input.value) ? toTimeFromSeconds(input.value) : null}
                              steps={{ minute: 15 }}
                              hourCycle={24}
                              validationState={meta.touched && meta.invalid ? 'invalid' : undefined}
                              className="w-full"
                            />
                          )}
                        </Field>

                        <OnChange name={`${dayTemplateName}.shiftDuration`}>
                          {(newDuration, oldDuration) => {
                            if (isCloning) {
                              // to prevent change of maximumPerDay values when cloning action has updated value of duration
                              return;
                            }

                            const dayTemplate = form.getState().values.dayTemplates[currentDayTemplateIndex];
                            if (!dayTemplate) {
                              return;
                            }

                            const updatedDayTemplate = clone(dayTemplate);
                            const shiftDuration = newDuration || 0;
                            let shouldUpdate = false;

                            updatedDayTemplate.scheduleActivitySettings?.forEach((activity, index) => {
                              if (activity.maximumPerDay !== shiftDuration) {
                                updatedDayTemplate.scheduleActivitySettings[index].maximumPerDay = shiftDuration;
                                shouldUpdate = true;
                              }
                            });
                            if (shouldUpdate) {
                              form.mutators.update('dayTemplates', currentDayTemplateIndex, updatedDayTemplate);
                              setMaximumActivitiesErrorVisible(oldDuration !== '' && newDuration !== '');
                            }
                          }}
                        </OnChange>
                      </Flex>

                      <Notice>{t('shiftTemplate.durationDescription')}</Notice>

                      {isMaximumActivitiesErrorVisible && (
                        <Notice>{t('shiftTemplate.durationChangedInformation')}</Notice>
                      )}

                      <FormSpy<ShiftTemplatesFormValues> subscription={{ values: true }}>
                        {({ values }) => {
                          let displayValue = '- -';
                          let endTime;
                          const currentDayTemplateValues = values.dayTemplates[currentDayTemplateIndex];

                          if (currentDayTemplateValues) {
                            const startTime = currentDayTemplateValues.shiftRange?.startTime;
                            const startTimeInSeconds = startTime ? toSecondsFromTime(parseTime(`${startTime}:00`)) : 0;
                            const duration = currentDayTemplateValues.shiftDuration;

                            const breaksDuration =
                              currentDayTemplateValues.breaks?.reduce((totalDuration, currentBreak) => {
                                if (
                                  currentBreak.duration &&
                                  currentBreak.type &&
                                  currentBreak.entitlementAfter !== undefined // could be 0 value
                                ) {
                                  return totalDuration + currentBreak.duration;
                                }

                                return totalDuration;
                              }, 0) ?? 0;

                            const endTimeValue = toTimeFromSeconds(
                              startTimeInSeconds + (duration ?? 0) + breaksDuration
                            );

                            if (startTime !== undefined && duration !== undefined) {
                              endTime = `${String(endTimeValue.hour).padStart(2, '0')}:${String(
                                endTimeValue.minute
                              ).padStart(2, '0')}`;
                              displayValue = timeFormatter.format(
                                toDateFromTime(endTimeValue).toDate(getLocalTimeZone())
                              );
                            }
                          }

                          if (currentDayTemplateValues.shiftRange?.endTime !== endTime) {
                            form.change(
                              `dayTemplates[${currentDayTemplateIndex}].shiftRange.endTime` as keyof ShiftTemplatesFormValues,
                              endTime
                            );
                          }

                          return (
                            <Stack direction="horizontal">
                              {t('shiftTemplate.calculatedShiftEnd')}:
                              <span data-test-id={SHIFT_END_TIME_INFO}>{displayValue}</span>
                            </Stack>
                          );
                        }}
                      </FormSpy>
                    </Stack>
                  </fieldset>
                  <fieldset>
                    <Stack direction="vertical" spacing={15} className="w-full">
                      <Heading as="h3" className="text-blue-700">
                        {t('shiftTemplate.timeBetweenShifts')}
                      </Heading>

                      <Field name={`${dayTemplateName}.shiftsMinGap`}>
                        {({ input, meta }) => (
                          <TimeField
                            testId={getFieldsInputTag('min-time-between-shifts')}
                            label={t('shiftTemplate.minimumTimeBetweenShifts')}
                            onBlur={input.onBlur}
                            onFocus={input.onFocus}
                            onChange={(value) => {
                              input.onChange(value ? toSecondsFromTime(value) : null);
                            }}
                            value={Number.isInteger(input.value) ? toTimeFromSeconds(input.value) : null}
                            steps={{ minute: 15 }}
                            hourCycle={24}
                            validationState={meta.touched && meta.invalid ? 'invalid' : undefined}
                            className="w-full"
                          />
                        )}
                      </Field>
                    </Stack>
                  </fieldset>
                  <fieldset data-test-id={SHIFT_BREAKS_CONTAINER}>
                    <Stack direction="vertical" spacing={15} className="w-full">
                      <Heading as="h3" className="text-blue-700">
                        {t('shiftTemplate.breaksSettings')}
                      </Heading>
                      <FieldArray name={`${dayTemplateName}.breaks`}>
                        {({ fields }) =>
                          fields.map((name, index) => {
                            const isLast = fields?.length === index + 1;

                            return (
                              <React.Fragment key={name}>
                                <BreakTemplate
                                  fieldPrefix={name}
                                  parentPrefix={`${dayTemplateName}.breaks`}
                                  index={index}
                                  isLast={fields.length === index + 1}
                                  form={form}
                                  currentDayTemplateIndex={currentDayTemplateIndex}
                                />
                                {!isLast && <Divider />}
                              </React.Fragment>
                            );
                          })
                        }
                      </FieldArray>
                      <Button
                        className="mis-auto"
                        variant="outline"
                        testId={ADD_NEW_BREAK_BUTTON}
                        onClick={() => form.mutators.push(`${dayTemplateName}.breaks`, initializeBreakValue())}
                      >
                        <PlusIcon /> {t('form.addNew')}
                      </Button>

                      <Field name={`${dayTemplateName}.breaksMinGap`}>
                        {({ input, meta }) => (
                          <TimeField
                            testId={getFieldsInputTag('min-time-between-breaks')}
                            label={t('shiftTemplate.breakMinimumTimeBetween')}
                            onBlur={input.onBlur}
                            onFocus={input.onFocus}
                            onChange={(value) => {
                              input.onChange(value ? toSecondsFromTime(value) : null);
                            }}
                            value={Number.isInteger(input.value) ? toTimeFromSeconds(input.value) : null}
                            steps={{ minute: 15 }}
                            hourCycle={24}
                            validationState={meta.touched && meta.invalid ? 'invalid' : undefined}
                            className="w-full"
                          />
                        )}
                      </Field>
                    </Stack>
                  </fieldset>
                  <fieldset data-test-id={SHIFT_SCHEDULE_ACTIVITIES_CONTAINER}>
                    <Stack direction="vertical" spacing={15} className="w-full">
                      <Heading as="h3" className="text-blue-700">
                        {t('schedule.activity', { count: 2 })}
                      </Heading>
                      <FormSpy<ShiftTemplatesFormValues> subscription={{ values: true }}>
                        {({ values }) => {
                          const currentDayTemplateValues = values.dayTemplates[currentDayTemplateIndex];
                          if (currentDayTemplateValues) {
                            const { shiftDuration } = currentDayTemplateValues;
                            const sumOfMinPerDay = currentDayTemplateValues.scheduleActivitySettings.reduce(
                              (sum, activity) => {
                                return sum + (activity.minimumPerDay ?? 0);
                              },
                              0
                            );

                            if (shiftDuration !== undefined && sumOfMinPerDay > shiftDuration) {
                              return (
                                <Alert color="error">
                                  {t('shiftTemplate.activitySumOfMinsCantBeHigherThanShiftDuration')}
                                </Alert>
                              );
                            }
                          }

                          return null;
                        }}
                      </FormSpy>
                      <FieldArray name={`${dayTemplateName}.scheduleActivitySettings`}>
                        {({ fields: activitiesFields }) => (
                          <Stack direction="vertical" spacing={25} className="w-full">
                            {activitiesFields.map((activityName, activityIndex) => {
                              const IconComponent = scheduleActivitiesConfig[activityIndex].iconComponent;

                              return (
                                <Stack key={activityName} direction="vertical" spacing={15}>
                                  <Stack>
                                    <IconComponent className="text-blue-700 text-lg" />
                                    {t(`${scheduleActivitiesConfig[activityIndex].labelKey}_channel`)}
                                  </Stack>
                                  <Flex gap={15}>
                                    <Field name={`${activityName}.minimumPerDay`}>
                                      {({ input, meta }) => (
                                        <TimeField
                                          testId={getFieldsInputTag(
                                            `${scheduleActivitiesConfig[activityIndex].fieldPrefix}-activity:min-per-day`
                                          )}
                                          label={t('time.minimumPerDay')}
                                          onBlur={input.onBlur}
                                          onFocus={input.onFocus}
                                          onChange={(value) => {
                                            input.onChange(value ? toSecondsFromTime(value) : null);
                                          }}
                                          value={Number.isInteger(input.value) ? toTimeFromSeconds(input.value) : null}
                                          steps={{ minute: 15 }}
                                          hourCycle={24}
                                          validationState={meta.touched && meta.invalid ? 'invalid' : undefined}
                                          className="w-full"
                                        />
                                      )}
                                    </Field>
                                    <Field name={`${activityName}.maximumPerDay`}>
                                      {({ input, meta }) => (
                                        <TimeField
                                          testId={getFieldsInputTag(
                                            `${scheduleActivitiesConfig[activityIndex].fieldPrefix}-activity:max-per-day`
                                          )}
                                          label={t('time.maximumPerDay')}
                                          onBlur={input.onBlur}
                                          onFocus={input.onFocus}
                                          onChange={(value) => {
                                            input.onChange(value ? toSecondsFromTime(value) : null);
                                          }}
                                          value={Number.isInteger(input.value) ? toTimeFromSeconds(input.value) : null}
                                          steps={{ minute: 15 }}
                                          hourCycle={24}
                                          validationState={meta.touched && meta.invalid ? 'invalid' : undefined}
                                          className="w-full"
                                        />
                                      )}
                                    </Field>
                                  </Flex>
                                  <FormSpy<ShiftTemplatesFormValues> subscription={{ values: true }}>
                                    {({ values }) => {
                                      const currentDayTemplateValues = values.dayTemplates[currentDayTemplateIndex];
                                      const currentActivity =
                                        currentDayTemplateValues.scheduleActivitySettings[activityIndex];
                                      const notices: JSX.Element[] = [];

                                      if (!currentDayTemplateValues && !currentActivity) {
                                        return null;
                                      }

                                      const minPerDay = currentActivity.minimumPerDay;
                                      const maxPerDay = currentActivity.maximumPerDay;
                                      const { shiftDuration } = currentDayTemplateValues;

                                      if (maxPerDay !== undefined && minPerDay !== undefined && maxPerDay < minPerDay) {
                                        notices.push(
                                          <Notice key="lower" color="error">
                                            {t('shiftTemplate.activityMaxCantBeLowerThanMin')}
                                          </Notice>
                                        );
                                      }

                                      if (
                                        maxPerDay !== undefined &&
                                        shiftDuration !== undefined &&
                                        maxPerDay > shiftDuration
                                      ) {
                                        notices.push(
                                          <Notice key="higher" color="error">
                                            {t('shiftTemplate.activityMaxCantBeHigherThanShiftDuration')}
                                          </Notice>
                                        );
                                      }

                                      return notices;
                                    }}
                                  </FormSpy>
                                </Stack>
                              );
                            })}
                          </Stack>
                        )}
                      </FieldArray>
                    </Stack>
                  </fieldset>
                </Stack>
              )
            )
          }
        </FieldArray>
      </Stack>
    </Box>
  );
};
